import { useEffect, useRef, useCallback, memo, useMemo, useState } from 'react'
import { Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import { useTranslation } from 'react-i18next'
import { toast } from '../../../services/toastService'
import { getEntityMarkerIconV2 } from '../../../config/mapIcons'
import { usePopupCoordinator } from '../../../hooks/usePopupCoordinator'
import EntityPopupContent from '../../../components/map/EntityPopupContent'
import BookingRequestModal from '../../../components/bookings/BookingRequestModal'
import { useMapAnalytics } from '../../../hooks/useMapAnalytics'

/**
 * NormalModeMarker - Regular entity marker with popup
 * Handles marker clicks, popup coordination, and entity isolation
 */
const NormalModeMarker = memo(function NormalModeMarker({
  entity,
  isSpotModeActive,
  user,
  navigate,
  setSpotModeEntity,
  setEntitySearchQuery,
  setSpotModeLocation,
  selectedEntityId,
  setSelectedEntityId,
  isEntityMode,
  onConfirmSuccess,
  markerRefs,
  allEntities,
  focusedEntityId,
  setFocusedEntityId,
  panelState,
  isolatedEntityId,
  setIsolatedEntityId,
  isImmersiveMode,
  onTabChange,
  isSearchPanelMinimized,
  setIsSearchPanelMinimized,
  currentOpenPopupRef,
  onPanelStateChange,
  isEventParticipant,
  onSpotEntity
}) {
  const { t } = useTranslation()
  const { currentLatitude: lat, currentLongitude: lng } = entity
  
  // All hooks must be called before any early returns
  const map = useMap()
  const confirmationCount = entity.confirmationCount || 0
  const isSelected = entity.id === selectedEntityId
  const markerRef = useRef(null)
  const { trackMarkerClick } = useMapAnalytics()
  
  // Use popup coordinator for state-driven coordination
  const { openPopup, closePopup, popupPadding } = usePopupCoordinator({
    panelState,
    onPanelStateChange
  })
  
  // Booking modal state — rendered outside <Popup> so it survives popup close
  const [bookingEntityId, setBookingEntityId] = useState(null)
  const [bookingEntityName, setBookingEntityName] = useState(null)

  const handleClosePopup = useCallback(() => {
    if (markerRef.current) markerRef.current.closePopup()
  }, [])

  const handleRequestBook = useCallback((entityId, entityName) => {
    handleClosePopup()
    setBookingEntityId(entityId)
    setBookingEntityName(entityName)
  }, [handleClosePopup])
  
  // Measure popup position when it opens - capture ACTUAL panel state at popup open time
  useEffect(() => {
    if (!markerRef.current) return
    
    const marker = markerRef.current
    const onPopupOpen = () => {
      setTimeout(() => {
        const popup = marker.getPopup()
        if (popup && map) {
          const popupElement = popup.getElement()
          if (popupElement) {
            // Get ACTUAL control panel element to measure real height
            const controlPanel = document.querySelector('.immersive-control-panel, [class*="control-panel"]')
            const actualPanelHeight = controlPanel ? controlPanel.getBoundingClientRect().height : 80
          }
        }
      }, 100) // Wait for popup animation
    }
    
    marker.on('popupopen', onPopupOpen)
    return () => {
      marker.off('popupopen', onPopupOpen)
    }
  }, [map, entity.name])

  // Determine if this marker should be dimmed
  const isFocused = focusedEntityId === null || focusedEntityId === entity.entityId
  const baseOpacity = isFocused ? (entity.markerOpacity || 1.0) : 0.25
  const effectiveOpacity = isSpotModeActive ? Math.min(baseOpacity, 0.3) : baseOpacity

  // Always use v2 brand-logo markers
  const markerIcon = useMemo(() => {
    return getEntityMarkerIconV2(entity, confirmationCount, effectiveOpacity)
  }, [confirmationCount, effectiveOpacity, entity])

  // Store ref for programmatic access
  useEffect(() => {
    if (markerRef.current && markerRefs) {
      markerRefs.current[entity.id] = markerRef.current
    }
    return () => {
      if (markerRefs) {
        delete markerRefs.current[entity.id]
      }
    }
  }, [entity.id, markerRefs])
  
  // Define all callback handlers before early return
  const handleClick = useCallback(() => {
    // Track marker click
    trackMarkerClick(entity)
    
    if (isSpotModeActive) {
      if (!user) {
        toast.info(t('toast.info.loginRequired'))
        if (isImmersiveMode && onTabChange) {
          onTabChange('auth')
        } else {
          navigate('/login', { state: { from: window.location.pathname } })
        }
        return
      }
      setSpotModeEntity(entity)
      setEntitySearchQuery(entity.name)
      setSpotModeLocation({ lat, lng })
      toast.success(t('toast.info.selected', { name: entity.name }))
    } else {
      // Close the currently open popup if any
      if (currentOpenPopupRef?.current) {
        currentOpenPopupRef.current.closePopup()
      }
      
      // Center map on clicked marker in the visible area (below control panel)
      if (map) {
        const mapSize = map.getSize()
        const markerPoint = map.latLngToContainerPoint([lat, lng])
        
        // Control panel height when minimized
        const controlPanelHeight = 80
        
        // Calculate center of VISIBLE map area (below control panel)
        const visibleHeight = mapSize.y - controlPanelHeight
        const visibleCenterY = controlPanelHeight + (visibleHeight / 2)
        
        // Position marker at visible center horizontally, slightly below center vertically
        const targetX = mapSize.x / 2
        const targetY = visibleCenterY + 50 // 50px below visible center for popup clearance
        
        // Calculate how many pixels we need to pan
        const panX = targetX - markerPoint.x
        const panY = targetY - markerPoint.y
        
        // Pan the map by the calculated amount.
        // Suppress all moveend events for 1.5s to cover the panBy animation,
        // Leaflet's popup auto-pan, and any panel resize moveend that follows.
        map._suppressBoundsUntil = Date.now() + 1500
        map.panBy([panX, panY], {
          animate: true,
          duration: 0.5
        })
      }
      
      // Use coordinated popup opening
      if (markerRef.current) {
        openPopup(markerRef.current, { requireMinimizedPanel: true })
        
        if (currentOpenPopupRef) {
          currentOpenPopupRef.current = markerRef.current
        }
      }
    }
  }, [isSpotModeActive, user, navigate, entity, lat, lng, setSpotModeEntity, setEntitySearchQuery, setSpotModeLocation, openPopup, currentOpenPopupRef, isImmersiveMode, onTabChange, map, trackMarkerClick, t])

  const handleAdd = useCallback((e) => {
    if (isSelected) {
      setTimeout(() => {
        e.target.openPopup()
        setSelectedEntityId(null)
      }, 100)
    }
  }, [isSelected, setSelectedEntityId])

  const handleRecenter = useCallback((e) => {
    e.stopPropagation()

    if (!map || !entity.entityId) return

    // Toggle isolation
    if (isolatedEntityId === entity.entityId) {
      setIsolatedEntityId(null)
      setFocusedEntityId(null)
    } else {
      setIsolatedEntityId(entity.entityId)
      setFocusedEntityId(entity.entityId)
    }

    // Find all markers for this entity
    const entityMarkers = allEntities.filter(e => e.entityId === entity.entityId)
    if (entityMarkers.length === 0) return

    // Create bounds from all entity markers
    const bounds = L.latLngBounds()
    entityMarkers.forEach(marker => {
      if (marker.currentLatitude && marker.currentLongitude) {
        bounds.extend([marker.currentLatitude, marker.currentLongitude])
      }
    })

    if (bounds.isValid()) {
      map.fitBounds(bounds, {
        padding: [80, 80],
        maxZoom: 16,
        animate: true,
        duration: 0.8
      })
    }
  }, [map, entity.entityId, allEntities, isolatedEntityId, setFocusedEntityId, setIsolatedEntityId])

  // Early return after all hooks have been called
  if (!lat || !lng) return null

  // Calendar badge icon (small calendar pin above marker for event participants)
  const eventBadgeIcon = isEventParticipant ? L.divIcon({
    className: '',
    html: '<div class="event-participant-badge">&#128197;</div>',
    iconSize: [20, 20],
    iconAnchor: [10, 36],
  }) : null

  // Live pulse icon (pulsing ring behind live entity marker)
  const livePulseIcon = entity.isLive ? L.divIcon({
    className: '',
    html: '<div class="live-entity-pulse"></div>',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  }) : null

  return (
    <>
      {isEventParticipant && (
        <Marker
          position={[lat, lng]}
          icon={eventBadgeIcon}
          interactive={false}
          zIndexOffset={100}
        />
      )}
      {entity.isLive && (
        <Marker
          position={[lat, lng]}
          icon={livePulseIcon}
          interactive={false}
          zIndexOffset={-100}
        />
      )}
      <Marker
      ref={markerRef}
      position={[lat, lng]}
      icon={markerIcon}
      title={entity.totalSightings > 1 ? `${entity.name} • Last seen ${entity.lastSeenAt ? new Date(entity.lastSeenAt).toLocaleDateString() : 'recently'} • ${entity.totalSightings} total sightings` : entity.name}
      eventHandlers={{
        click: (e) => {
          e.target.closePopup()
          handleClick()
        },
        add: handleAdd,
        popupopen: (e) => {
          // Only allow popups through our coordinator
        }
      }}
    >
      <Popup 
        key={`popup-${entity.id}`}
        className="custom-popup draggable-popup" 
        autoClose={false} 
        closeOnClick={false} 
        autoOpen={false}
        autoPan={true}
      >
        <EntityPopupContent
          entity={entity}
          variant="normal"
          confirmationCount={confirmationCount}
          isEntityMode={isEntityMode}
          sightingId={entity.id}
          sightingUserId={entity.currentSightingUserId}
          user={user}
          userLocation={user?.location}
          isImmersiveMode={isImmersiveMode}
          onTabChange={onTabChange}
          onClosePopup={handleClosePopup}
          onSpotEntity={onSpotEntity}
          onRequestBook={handleRequestBook}
        />
      </Popup>
    </Marker>
    {bookingEntityId && (
      <BookingRequestModal
        entityId={bookingEntityId}
        entityName={bookingEntityName}
        onClose={() => { setBookingEntityId(null); setBookingEntityName(null) }}
      />
    )}
    </>
  )
}, (prevProps, nextProps) => {
  // Custom comparison to optimize re-renders
  return (
    prevProps.entity.id === nextProps.entity.id &&
    prevProps.entity.confirmationCount === nextProps.entity.confirmationCount &&
    prevProps.entity.currentLatitude === nextProps.entity.currentLatitude &&
    prevProps.entity.currentLongitude === nextProps.entity.currentLongitude &&
    prevProps.entity.currentSightingUserId === nextProps.entity.currentSightingUserId &&
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.selectedEntityId === nextProps.selectedEntityId &&
    prevProps.focusedEntityId === nextProps.focusedEntityId &&
    prevProps.isolatedEntityId === nextProps.isolatedEntityId &&
    prevProps.isSpotModeActive === nextProps.isSpotModeActive &&
    prevProps.panelState === nextProps.panelState &&
    prevProps.entity.isLive === nextProps.entity.isLive
  )
})

export default NormalModeMarker
